import { useEffect, useMemo, useState } from "react";
import {
  ArrowLeft, Search, FileText, CheckCircle2, Users, X, Check,
  ChevronRight, Layers, SlidersHorizontal, Trash2,
} from "lucide-react";
import {
  SPECIALTIES, loadSpecialtyCatalogue, type SpecialtyWithCounts,
} from "@/lib/ngap-specialities";
import {
  DOCTOR_DIRECTORY,
  loadAssignments, saveAssignments, type AssignmentMap,
} from "@/lib/ngap-data";
import type { NgapAct } from "@/lib/ngap-catalogue";

const TYPE_LABEL: Record<string, string> = {
  autre: "Acte", reduction: "Réduction", osteosynthese: "Ostéosynthèse",
  appareillage: "Appareillage", suture: "Suture", incision: "Incision",
  dentaire: "Dentaire", injection: "Injection", seance: "Séance",
  test: "Test", biologie: "Biologie", ponction: "Ponction", exerese: "Exérèse",
  pose: "Pose", chirurgie: "Chirurgie", anesthesie: "Anesthésie",
  biopsie: "Biopsie", mesure: "Mesure", exploration: "Exploration",
  perfusion: "Perfusion", transfusion: "Transfusion", ligature: "Ligature",
  enregistrement: "Enregistrement", intubation: "Intubation",
  echographie: "Échographie", pansement: "Pansement", dialyse: "Dialyse",
  audiometrie: "Audiométrie", endoscopie: "Endoscopie", irm: "IRM",
  reanimation: "Réanimation", spirometrie: "Spirométrie",
  radiographie: "Radiographie", ecg: "ECG", surveillance: "Surveillance",
  visite: "Visite", lavage: "Lavage", obstetrique: "Obstétrique",
  cesarienne: "Césarienne", kinesitherapie: "Kinésithérapie",
  scanner: "Scanner", scintigraphie: "Scintigraphie",
};

const LS_TAB = "dikdoc.ngap.activeSpec";
const LS_SUB = "dikdoc.ngap.activeSub";

function escapeRegExp(s: string) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
function Highlight({ text, term }: { text: string; term: string }) {
  const t = term.trim();
  if (!t) return <>{text}</>;
  const parts = text.split(new RegExp(`(${escapeRegExp(t)})`, "ig"));
  return (
    <>
      {parts.map((p, i) =>
        p.toLowerCase() === t.toLowerCase()
          ? <mark key={i} className="bg-accent/30 text-foreground rounded px-0.5">{p}</mark>
          : <span key={i}>{p}</span>,
      )}
    </>
  );
}

export default function CotationsView({ onBack }: { onBack: () => void }) {
  const [activeId, setActiveId] = useState<string>(() => {
    if (typeof window === "undefined") return SPECIALTIES[0].id;
    const v = window.localStorage.getItem(LS_TAB);
    return v && SPECIALTIES.some((s) => s.id === v) ? v : SPECIALTIES[0].id;
  });
  const [activeSub, setActiveSub] = useState<string>(() => {
    if (typeof window === "undefined") return "all";
    return window.localStorage.getItem(LS_SUB) || "all";
  });
  const [query, setQuery] = useState("");
  const [codeQuery, setCodeQuery] = useState("");
  const [labelQuery, setLabelQuery] = useState("");
  const [minK, setMinK] = useState<string>("");
  const [maxK, setMaxK] = useState<string>("");
  const [showFilters, setShowFilters] = useState(false);
  const [assignments, setAssignments] = useState<AssignmentMap>(() => loadAssignments());
  const [drawer, setDrawer] = useState<{ act: NgapAct } | null>(null);
  // The catalogue is loaded per specialty, on demand: the 981 KB JSON that
  // used to sit in the root bundle is now one lazy chunk per specialty.
  const [acts, setActs] = useState<NgapAct[]>([]);
  const [subSpecialties, setSubSpecialties] = useState<{ id: string; count: number }[]>([]);
  const [actsLoading, setActsLoading] = useState(false);

  useEffect(() => { try { window.localStorage.setItem(LS_TAB, activeId); } catch {} }, [activeId]);
  useEffect(() => { try { window.localStorage.setItem(LS_SUB, activeSub); } catch {} }, [activeSub]);

  const active = SPECIALTIES.find((s) => s.id === activeId)!;
  const fullCodeId = (cid: string) => `${active.id}::${cid}`;

  useEffect(() => {
    let cancelled = false;
    setActsLoading(true);
    loadSpecialtyCatalogue(active.id)
      .then(({ acts: loaded, subSpecialties: subs }) => {
        if (cancelled) return;
        setActs(loaded);
        setSubSpecialties(subs);
      })
      .catch(() => {
        if (!cancelled) {
          setActs([]);
          setSubSpecialties([]);
        }
      })
      .finally(() => {
        if (!cancelled) setActsLoading(false);
      });
    return () => { cancelled = true; };
  }, [active.id]);

  const subList = useMemo(
    () => [{ id: "all", count: acts.length }, ...subSpecialties],
    [acts.length, subSpecialties],
  );

  const codes = useMemo(
    () =>
      !activeSub || activeSub === "all"
        ? acts
        : acts.filter((a) => (a.subSpecialty || "Général") === activeSub),
    [acts, activeSub],
  );
  const filteredCodes = useMemo(() => {
    const q = query.trim().toLowerCase();
    const cq = codeQuery.trim().toLowerCase();
    const lq = labelQuery.trim().toLowerCase();
    const min = minK ? parseFloat(minK) : null;
    const max = maxK ? parseFloat(maxK) : null;
    return codes.filter((c) => {
      if (q && !(c.code.toLowerCase().includes(q) || c.label.toLowerCase().includes(q))) return false;
      if (cq && !c.code.toLowerCase().includes(cq)) return false;
      if (lq && !c.label.toLowerCase().includes(lq)) return false;
      if (min !== null && !isNaN(min) && c.coefficient < min) return false;
      if (max !== null && !isNaN(max) && c.coefficient > max) return false;
      return true;
    });
  }, [codes, query, codeQuery, labelQuery, minK, maxK]);

  const assignedDoctorsFor = (codeId: string) =>
    DOCTOR_DIRECTORY.filter((d) => (assignments[d.id] ?? []).includes(fullCodeId(codeId)));

  const toggle = (doctorId: string, codeId: string) => {
    setAssignments((prev) => {
      const list = new Set(prev[doctorId] ?? []);
      const full = fullCodeId(codeId);
      if (list.has(full)) list.delete(full); else list.add(full);
      const next = { ...prev, [doctorId]: Array.from(list) };
      saveAssignments(next);
      return next;
    });
  };

  const onPickSpecialty = (s: SpecialtyWithCounts) => {
    setActiveId(s.id);
    setActiveSub("all");
  };

  const clearFilters = () => { setQuery(""); setCodeQuery(""); setLabelQuery(""); setMinK(""); setMaxK(""); };
  const hasFilters = !!(query || codeQuery || labelQuery || minK || maxK);

  return (
    <div className="flex flex-col gap-5">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={onBack} className="icon-btn"><ArrowLeft size={15} /></button>
          <div className="min-w-0">
            <h2 className="text-xl font-semibold tracking-tight">Cotations NGAP</h2>
            <div className="truncate text-xs text-muted-foreground">
              NGAP Maroc · {SPECIALTIES.length} spécialités · {SPECIALTIES.reduce((a, s) => a + s.count, 0)} actes
            </div>
          </div>
        </div>
        <div className="hidden sm:flex items-center gap-2 rounded-full bg-surface-2 border border-border px-3 py-1.5 text-xs text-muted-foreground">
          <FileText size={13} /> Arrêté 177-06 + Arrêté 1796-03 (Biologie)
        </div>
      </div>

      {/* Specialty pill tabs */}
      <div role="tablist" aria-label="Spécialités NGAP" className="flex flex-wrap items-center gap-2 rounded-2xl border border-border bg-surface-2/40 p-1.5">
        {SPECIALTIES.map((s) => {
          const isActive = s.id === activeId;
          return (
            <button
              key={s.id}
              role="tab"
              aria-selected={isActive}
              onClick={() => onPickSpecialty(s)}
              className={[
                "inline-flex items-center gap-2 rounded-full h-9 px-4 text-[13px] font-medium whitespace-nowrap",
                "transition-all duration-200 ease-out active:scale-[0.97]",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60",
                isActive
                  ? "bg-background text-foreground shadow-[0_4px_14px_-4px_rgba(0,0,0,0.35)]"
                  : "text-foreground/60 hover:text-foreground hover:bg-surface-3/60",
              ].join(" ")}
            >
              <span>{s.label}</span>
              <span className={[
                "rounded-full px-1.5 py-0.5 text-[10px] font-semibold leading-none",
                isActive ? "bg-foreground/10 text-foreground" : "bg-surface-3 text-muted-foreground",
              ].join(" ")}>
                {s.count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Speciality header */}
      <div className="card-surface rounded-2xl p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span className="rounded-full bg-accent/15 text-accent border border-accent/30 px-2 py-0.5">{active.label}</span>
              <ChevronRight size={11} />
              <span>{active.scope}</span>
            </div>
            <h3 className="mt-2 text-lg font-semibold">{active.label}</h3>
            <p className="text-sm text-muted-foreground">{acts.length} actes · {subSpecialties.length} sous-spécialité{subSpecialties.length > 1 ? "s" : ""}</p>
          </div>
          <div className="flex items-center gap-2 w-full max-w-md">
            <div className="flex items-center gap-2 rounded-xl border border-border bg-surface-2 px-3 py-2 flex-1">
              <Search size={13} className="text-muted-foreground" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Recherche globale (code ou libellé)…"
                className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
              />
              {query && (
                <button onClick={() => setQuery("")} className="text-muted-foreground hover:text-foreground"><X size={13} /></button>
              )}
            </div>
            <button
              onClick={() => setShowFilters((v) => !v)}
              className={[
                "rounded-xl border px-3 py-2 text-xs font-medium inline-flex items-center gap-1.5 transition",
                showFilters || hasFilters
                  ? "bg-foreground text-background border-foreground"
                  : "bg-surface-2 text-muted-foreground border-border hover:text-foreground",
              ].join(" ")}
            >
              <SlidersHorizontal size={12} /> Filtres
            </button>
          </div>
        </div>

        {/* Advanced filters */}
        {showFilters && (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-2 rounded-xl border border-border bg-surface-2/60 p-3">
            <FilterInput label="Code" value={codeQuery} onChange={setCodeQuery} placeholder="ex: K, ZZ100…" />
            <FilterInput label="Libellé" value={labelQuery} onChange={setLabelQuery} placeholder="ex: échographie…" />
            <FilterInput label="K min" value={minK} onChange={setMinK} placeholder="0" type="number" />
            <FilterInput label="K max" value={maxK} onChange={setMaxK} placeholder="∞" type="number" />
            <div className="md:col-span-4 flex items-center justify-between text-[11px] text-muted-foreground">
              <span>{filteredCodes.length} acte{filteredCodes.length > 1 ? "s" : ""} correspondant{filteredCodes.length > 1 ? "s" : ""}</span>
              {hasFilters && (
                <button onClick={clearFilters} className="text-foreground hover:underline">Réinitialiser</button>
              )}
            </div>
          </div>
        )}

        {/* Sub-specialty chips */}
        {subList.length > 1 && (
          <div className="mt-4 flex flex-wrap items-center gap-1.5">
            <Layers size={12} className="text-muted-foreground mr-1" />
            {subList.map((s) => {
              const isActive = s.id === activeSub;
              return (
                <button
                  key={s.id}
                  onClick={() => setActiveSub(s.id)}
                  className={[
                    "rounded-full h-7 px-3 text-[11px] font-medium border transition",
                    isActive
                      ? "bg-accent text-accent-foreground border-accent"
                      : "bg-surface-2 text-muted-foreground border-border hover:text-foreground hover:bg-surface-3",
                  ].join(" ")}
                >
                  {s.id === "all" ? "Tous" : s.id}
                  <span className="ml-1.5 opacity-70">{s.count}</span>
                </button>
              );
            })}
          </div>
        )}

        {/* Codes grid */}
        <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          {filteredCodes.length === 0 && (
            <div className="md:col-span-2 xl:col-span-3 rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
              Aucun acte ne correspond à votre recherche.
            </div>
          )}
          {filteredCodes.slice(0, 240).map((c) => {
            const docs = assignedDoctorsFor(c.code);
            const codeHl = codeQuery || query;
            const labelHl = labelQuery || query;
            return (
              <article key={c.code} className="group rounded-xl border border-border bg-surface-2 p-4 transition hover:border-foreground/30 hover:bg-surface-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="rounded-md bg-foreground/10 px-2 py-0.5 font-mono text-[11px] font-semibold tracking-wide">
                        <Highlight text={c.code} term={codeHl} />
                      </span>
                      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{TYPE_LABEL[c.actType ?? ""] ?? c.actType ?? ""}</span>
                    </div>
                    <p className="mt-2 text-sm font-medium leading-snug line-clamp-3">
                      <Highlight text={c.label} term={labelHl} />
                    </p>
                    {c.article && (
                      <p className="mt-1 text-[11px] text-muted-foreground line-clamp-2">{c.article}</p>
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-base font-semibold text-success">K {c.coefficient}</div>
                    {c.secondaryCoefficient ? (
                      <div className="text-[10px] text-muted-foreground">+K {c.secondaryCoefficient}</div>
                    ) : (
                      <div className="text-[10px] text-muted-foreground">coefficient</div>
                    )}
                  </div>
                </div>
                <div className="mt-3 flex items-center justify-between border-t border-border pt-3">
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Users size={12} />
                    {docs.length} médecin{docs.length > 1 ? "s" : ""} assigné{docs.length > 1 ? "s" : ""}
                  </div>
                  <button
                    onClick={() => setDrawer({ act: c })}
                    className="inline-flex items-center gap-1.5 rounded-full bg-accent text-accent-foreground px-3 py-1 text-xs font-medium hover:opacity-90"
                  >
                    <CheckCircle2 size={12} /> Assigner
                  </button>
                </div>
              </article>
            );
          })}
        </div>
        {filteredCodes.length > 240 && (
          <div className="mt-3 text-center text-xs text-muted-foreground">
            Affichage de 240 / {filteredCodes.length} actes — affinez votre recherche.
          </div>
        )}
      </div>

      {drawer && (
        <AssignmentDrawer
          act={drawer.act}
          eligibleSpecs={active.doctorSpecs}
          assignments={assignments}
          fullCodeId={fullCodeId(drawer.act.code)}
          onToggle={(docId) => toggle(docId, drawer.act.code)}
          onClose={() => setDrawer(null)}
        />
      )}
    </div>
  );
}

function FilterInput({
  label, value, onChange, placeholder, type = "text",
}: { label: string; value: string; onChange: (v: string) => void; placeholder?: string; type?: string }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="rounded-lg border border-border bg-surface-2 px-2.5 py-1.5 text-sm outline-none focus:border-foreground/40"
      />
    </label>
  );
}

function AssignmentDrawer({
  act, eligibleSpecs, assignments, fullCodeId, onToggle, onClose,
}: {
  act: NgapAct;
  eligibleSpecs: string[];
  assignments: AssignmentMap;
  fullCodeId: string;
  onToggle: (doctorId: string) => void;
  onClose: () => void;
}) {
  const [filter, setFilter] = useState<"all" | "eligible">("eligible");
  const assigned = DOCTOR_DIRECTORY.filter((d) => (assignments[d.id] ?? []).includes(fullCodeId));
  const list = DOCTOR_DIRECTORY.filter((d) =>
    filter === "all" ? true : eligibleSpecs.length === 0 || eligibleSpecs.includes(d.speciality),
  );
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full sm:max-w-lg card-surface rounded-t-2xl sm:rounded-2xl border border-border">
        <div className="flex items-start justify-between p-5 border-b border-border">
          <div className="min-w-0">
            <div className="text-xs text-muted-foreground">Assigner aux médecins</div>
            <h4 className="mt-0.5 text-base font-semibold">
              <span className="font-mono text-xs bg-foreground/10 rounded px-1.5 py-0.5 mr-2">{act.code}</span>
              {act.label}
            </h4>
            <div className="mt-1 text-xs text-success">K {act.coefficient}{act.secondaryCoefficient ? ` + K ${act.secondaryCoefficient}` : ""}</div>
          </div>
          <button onClick={onClose} className="icon-btn"><X size={14} /></button>
        </div>

        {/* Currently assigned doctors */}
        <div className="px-5 pt-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] uppercase tracking-wide text-muted-foreground">Médecins assignés</span>
            <span className="text-[11px] text-muted-foreground">{assigned.length}</span>
          </div>
          {assigned.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border p-3 text-center text-xs text-muted-foreground">
              Aucun médecin assigné à cet acte.
            </div>
          ) : (
            <ul className="flex flex-wrap gap-1.5">
              {assigned.map((d) => (
                <li key={d.id}>
                  <button
                    onClick={() => onToggle(d.id)}
                    className="group inline-flex items-center gap-1.5 rounded-full bg-success/15 border border-success/30 text-success pl-2.5 pr-1.5 py-1 text-xs hover:bg-success/25"
                    title="Retirer l'assignation"
                  >
                    <span className="font-medium">{d.name}</span>
                    <span className="grid place-items-center h-4 w-4 rounded-full bg-success/30 text-success group-hover:bg-success group-hover:text-success-foreground transition">
                      <Trash2 size={9} />
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="px-5 pt-4 pb-2 flex items-center gap-2">
          <span className="text-[11px] uppercase tracking-wide text-muted-foreground mr-1">Ajouter :</span>
          {(["eligible", "all"] as const).map((k) => (
            <button
              key={k}
              onClick={() => setFilter(k)}
              className={[
                "rounded-full px-3 py-1 text-xs border transition",
                filter === k
                  ? "bg-foreground text-background border-foreground"
                  : "bg-surface-2 text-muted-foreground border-border hover:text-foreground",
              ].join(" ")}
            >
              {k === "eligible" ? "Spécialités éligibles" : "Tous les médecins"}
            </button>
          ))}
        </div>
        <ul className="p-3 max-h-[45vh] overflow-y-auto">
          {list.length === 0 && (
            <li className="rounded-xl border border-dashed border-border p-6 text-center text-xs text-muted-foreground">
              Aucun médecin pour ces spécialités.
            </li>
          )}
          {list.map((d) => {
            const checked = (assignments[d.id] ?? []).includes(fullCodeId);
            return (
              <li key={d.id}>
                <button
                  onClick={() => onToggle(d.id)}
                  className={[
                    "flex w-full items-center gap-3 rounded-xl border p-3 text-left transition",
                    checked
                      ? "border-success/50 bg-success/10"
                      : "border-transparent hover:bg-surface-2",
                  ].join(" ")}
                >
                  <span className={[
                    "grid h-9 w-9 place-items-center rounded-full text-sm font-semibold",
                    checked ? "bg-success text-success-foreground" : "bg-surface-3 text-muted-foreground",
                  ].join(" ")}>
                    {checked ? <Check size={15} /> : d.name.split(" ")[1]?.[0] ?? "D"}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{d.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{d.speciality}</p>
                  </div>
                  {checked && <span className="text-[10px] text-success font-medium">Assigné</span>}
                </button>
              </li>
            );
          })}
        </ul>
        <div className="border-t border-border p-3 flex justify-end">
          <button onClick={onClose} className="rounded-full bg-foreground text-background px-4 py-1.5 text-xs font-medium">Terminé</button>
        </div>
      </div>
    </div>
  );
}
